import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { FaPlus, FaEdit, FaTrash, FaTrophy } from '../../components/Icons';
import { SectionHeaderComp } from './LastresLigaPage';
import { LigaServiceFirestore } from '../../services/LigaServiceFirestore';
import { theme } from '../../theme';

const Container = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${theme.colors.background.default};
`;

const Title = styled.h1`
  color: ${theme.colors.primary.main};
  margin-bottom: 32px;
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 2.2rem;
  position: relative;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 16px;
    color: ${theme.colors.secondary.main};
  }
`;

const Button = styled.button`
  background-color: ${theme.colors.secondary.main};
  color: ${theme.colors.secondary.contrastText};
  border: none;
  border-radius: ${theme.borderRadius.small};
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: all 0.3s ease;
  box-shadow: ${theme.shadows.small};
  
  &:hover {
    background-color: ${theme.colors.secondary.dark};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  svg {
    margin-right: 10px;
    font-size: 1.1rem;
  }
`;

const TableContainer = styled.div`
  overflow: hidden;
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  background-color: ${theme.colors.background.paper};
  margin-bottom: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: ${theme.colors.background.paper};
`;

const Th = styled.th`
  background-color: ${theme.colors.primary.main};
  color: ${theme.colors.primary.contrastText};
  text-align: left;
  padding: 16px;
  font-weight: ${theme.typography.fontWeights.semibold};
  position: sticky;
  top: 0;
  z-index: 10;
  
  &:first-child {
    border-top-left-radius: ${theme.borderRadius.small};
  }
  
  &:last-child {
    border-top-right-radius: ${theme.borderRadius.small};
  }
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  color: ${theme.colors.text.primary};
  transition: background-color 0.2s ease;
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin-right: 12px;
  color: ${theme.colors.primary.main};
  font-size: 1.1rem;
  padding: 8px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${theme.colors.secondary.main};
    background-color: rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  color: ${theme.colors.text.secondary};
  font-size: 1.1rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  svg {
    font-size: 3rem;
    color: ${theme.colors.secondary.main};
    margin-bottom: 16px;
    opacity: 0.7;
  }
`;

// Modal de confirmación
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background-color: ${theme.colors.background.paper};
  padding: 24px;
  border-radius: ${theme.borderRadius.medium};
  width: 450px;
  box-shadow: ${theme.shadows.large};
  animation: slideUp 0.3s ease;
  
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ModalTitle = styled.h3`
  margin-top: 0;
  color: ${theme.colors.primary.main};
  font-size: 1.5rem;
  font-weight: ${theme.typography.fontWeights.semibold};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 12px;
    color: ${theme.colors.error.main};
  }
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  gap: 12px;
`;

const CancelButton = styled.button`
  background-color: ${theme.colors.background.default};
  color: ${theme.colors.text.primary};
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.small};
  padding: 10px 20px;
  cursor: pointer;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${theme.colors.background.default};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.small};
    background-color: #5a6268;
  }
`;

const DeleteButton = styled(Button)`
  background-color: #dc3545;
  
  &:hover {
    background-color: #c82333;
  }
`;

const LigasList: React.FC = () => {
  const { data, loadData } = useContext(AppContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ligaToDelete, setLigaToDelete] = useState<string | null>(null);
  
  // Función para manejar la eliminación de una liga
  const handleDelete = (ligaId: string) => {
    setLigaToDelete(ligaId);
    setShowDeleteModal(true);
  };
  
  // Función para confirmar la eliminación
  const confirmDelete = async () => {
    if (ligaToDelete) {
      await LigaServiceFirestore.delete(ligaToDelete);
      await loadData(); // Recargar los datos después de eliminar
      setShowDeleteModal(false);
      setLigaToDelete(null);
    }
  };
  
  // Función para cancelar la eliminación
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setLigaToDelete(null);
  };
  
  return (
    <Container>
      <SectionHeaderComp icon={<FaTrophy style={{ color: '#e4312e' }} />}>
        Gestión de Ligas
      </SectionHeaderComp>
      
      <Link to="/ligas/new">
        <Button>
          <FaPlus /> Nueva Liga
        </Button>
      </Link>
      
      {data.ligas.length > 0 ? (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th>Nombre</Th>
                <Th>Descripción</Th>
                <Th>Carreras</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {data.ligas.map(liga => (
                <tr key={liga.id}>
                  <Td>{liga.nombre}</Td>
                  <Td>{liga.descripcion}</Td>
                  <Td>{liga.carreras.length}</Td>
                  <Td>
                    <Link to={`/ligas/${liga.id}`}>
                      <ActionButton title="Editar liga">
                        <FaEdit />
                      </ActionButton>
                    </Link>
                    <ActionButton onClick={() => handleDelete(liga.id)} title="Eliminar liga">
                      <FaTrash />
                    </ActionButton>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyMessage>
          <FaTrophy />
          <p>No hay ligas registradas. Crea una nueva liga para comenzar.</p>
        </EmptyMessage>
      )}
      
      {/* Modal de confirmación para eliminar */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle><FaTrash /> Confirmar eliminación</ModalTitle>
            <p>¿Estás seguro de que deseas eliminar esta liga? Esta acción no se puede deshacer.</p>
            <p>Se eliminarán también todas las carreras asociadas a esta liga y sus resultados.</p>
            <ModalButtons>
              <CancelButton onClick={cancelDelete}>Cancelar</CancelButton>
              <DeleteButton onClick={confirmDelete}>Eliminar</DeleteButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default LigasList;
